import { inject, Injectable, signal } from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  setDoc,
} from '@angular/fire/firestore';
import { AuthStore } from '../auth/auth.store';
import { TodoItem, TodoStatus } from '../models/todo.model';

@Injectable({ providedIn: 'root' })
export class TodoService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);

  private readonly _todos = signal<TodoItem[]>([]);
  private readonly _loading = signal(false);

  readonly todos = this._todos.asReadonly();
  readonly loading = this._loading.asReadonly();

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) throw new Error('No tenant selected');
    return id;
  }

  private get userId(): string {
    const id = this.authStore.userId();
    if (!id) throw new Error('No user logged in');
    return id;
  }

  async loadTodos(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/todos`);
      const q = query(
        ref,
        where('assigneeId', '==', this.userId),
        orderBy('createdAt', 'desc'),
        limit(200)
      );
      const snap = await getDocs(q);
      this._todos.set(snap.docs.map((docSnap) => this.mapTodo(docSnap.id, docSnap.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async createTodo(title: string): Promise<void> {
    const trimmed = title.trim();
    if (!trimmed) return;

    const ref = doc(collection(this.firestore, `tenants/${this.tenantId}/todos`));
    await setDoc(ref, {
      title: trimmed,
      status: 'open',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: this.userId,
      assigneeId: this.userId,
    });

    await this.loadTodos();
  }

  async updateStatus(todoId: string, status: TodoStatus): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/todos/${todoId}`);
    await updateDoc(ref, {
      status,
      updatedAt: serverTimestamp(),
    });

    this._todos.update((items) =>
      items.map((item) => (item.id === todoId ? { ...item, status } : item))
    );
  }

  private mapTodo(id: string, data: Record<string, unknown>): TodoItem {
    return {
      id,
      tenantId: this.tenantId,
      title: (data['title'] as string) ?? '',
      status: (data['status'] as TodoStatus) ?? 'open',
      createdAt: this.parseTimestamp(data['createdAt']) ?? new Date(),
      updatedAt: this.parseTimestamp(data['updatedAt']),
      createdBy: (data['createdBy'] as string) ?? '',
      assigneeId: (data['assigneeId'] as string) ?? '',
    };
  }

  private parseTimestamp(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    const maybe = value as { toDate?: () => Date };
    if (typeof maybe.toDate === 'function') {
      return maybe.toDate();
    }
    return null;
  }
}
